import React, { useEffect, useMemo, useState } from 'react'
import { adminService } from '../../services/admin'

type AdminRecord = {
  email: string
  role?: string
  firstName?: string
  lastName?: string
}

type CorporateAssignment = {
  corporateEmail?: string
  locationKey?: string
  assignedAt?: string
}

type LocationRow = {
  key: string
  label?: string
}

type AdminBreadcrumb = {
  id?: string
  recordedAt?: string
  phone?: string
  workerName?: string
  locationLabel?: string
  lat?: number | null
  lon?: number | null
}

export const SuperAdmin: React.FC = () => {
  const [admins, setAdmins] = useState<AdminRecord[]>([])
  const [assignments, setAssignments] = useState<CorporateAssignment[]>([])
  const [locations, setLocations] = useState<LocationRow[]>([])
  const [profileEmail, setProfileEmail] = useState('')
  const [profileFirst, setProfileFirst] = useState('')
  const [profileLast, setProfileLast] = useState('')
  const [profileRole, setProfileRole] = useState('admin')
  const [corporateEmail, setCorporateEmail] = useState('')
  const [assignmentText, setAssignmentText] = useState('')
  const [reportLocation, setReportLocation] = useState('')
  const [reportContent, setReportContent] = useState('')
  const [payrollCsv, setPayrollCsv] = useState('')
  const [breadcrumbEmail, setBreadcrumbEmail] = useState('')
  const [breadcrumbLocation, setBreadcrumbLocation] = useState('')
  const [breadcrumbRows, setBreadcrumbRows] = useState<AdminBreadcrumb[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('')

  const loadData = async () => {
    setLoading(true)
    setError('')
    try {
      const [adminsResponse, assignmentsResponse, locationsResponse] = await Promise.all([
        adminService.getUsers(),
        adminService.getCorporateAssignments(),
        adminService.getLocations(),
      ])
      setAdmins((adminsResponse?.data?.records || []) as AdminRecord[])
      setAssignments((assignmentsResponse?.data?.records || []) as CorporateAssignment[])
      setLocations((locationsResponse?.data?.locations || []) as LocationRow[])
      setReportLocation((current) => current || locationsResponse?.data?.locations?.[0]?.key || '')
      setBreadcrumbLocation((current) => current || locationsResponse?.data?.locations?.[0]?.key || '')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load enterprise admin data.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  const assignmentCountByEmail = useMemo(() => {
    const counts = new Map<string, number>()
    for (const assignment of assignments) {
      const email = (assignment.corporateEmail || '').toLowerCase()
      counts.set(email, (counts.get(email) || 0) + 1)
    }
    return counts
  }, [assignments])

  const saveProfile = async () => {
    if (!profileEmail) {
      setError('Email is required.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await adminService.updateUser(profileEmail, { firstName: profileFirst, lastName: profileLast, role: profileRole })
      setStatus('Admin profile saved.')
      await loadData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save admin profile.')
    } finally {
      setLoading(false)
    }
  }

  const saveCorporate = async () => {
    if (!corporateEmail) {
      setError('Corporate email is required.')
      return
    }
    setLoading(true)
    setError('')
    try {
      await adminService.createCorporateUser({ email: corporateEmail, role: 'supervisor', active: true })
      const locationKeys = assignmentText.split(',').map((item) => item.trim()).filter(Boolean)
      if (locationKeys.length) {
        await adminService.assignLocationsToCorporateUser(corporateEmail, locationKeys)
      }
      setStatus('Corporate user saved.')
      await loadData()
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to save corporate user.')
    } finally {
      setLoading(false)
    }
  }

  const uploadReport = async () => {
    if (!reportLocation || !reportContent.trim()) return
    setLoading(true)
    setError('')
    try {
      const response = await adminService.uploadLocationJobReport({
        locationKey: reportLocation,
        fileName: `job-report-${Date.now()}.txt`,
        contents: reportContent,
        reportDate: new Date().toISOString(),
      })
      setStatus(`Job report uploaded: ${response.data.reportId || ''}`)
      setReportContent('')
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to upload job report.')
    } finally {
      setLoading(false)
    }
  }

  const generatePayroll = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.generatePayrollExport({})
      setPayrollCsv(response.data.csv || '')
      setStatus(`${response.data.rows?.length || 0} payroll rows generated.`)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to generate payroll export.')
    } finally {
      setLoading(false)
    }
  }

  const loadBreadcrumbs = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminService.listAdminBreadcrumbs({
        email: breadcrumbEmail || undefined,
        locationKey: breadcrumbLocation || undefined,
        limit: 100,
      })
      setBreadcrumbRows((response.data.breadcrumbs || []) as AdminBreadcrumb[])
      setStatus(`${response.data.totalBreadcrumbs || 0} breadcrumb records found.`)
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Unable to load breadcrumb records.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <section className="panel workspace-panel stack" data-testid="workspace-panel">
      <div className="stack" style={{ gap: '.4rem' }}>
        <h2 className="heading-lg">Super admin workspace</h2>
        <p className="text-muted">Manage admin profiles, corporate assignments, job reports, and payroll exports.</p>
      </div>

      <div className="inline-group">
        <button type="button" className="btn btn-secondary" onClick={loadData} disabled={loading}>
          {loading ? 'Refreshing...' : 'Refresh data'}
        </button>
      </div>

      {error ? <p className="error-text">{error}</p> : null}
      {status ? <p className="helper">{status}</p> : null}

      <div className="panel workspace-panel" style={{ background: 'var(--surface-muted)', overflowX: 'auto' }}>
        <h3 style={{ marginTop: 0 }}>Enterprise admins</h3>
        <table className="workspace-table" data-testid="super-admin-table">
          <thead><tr><th>Admin</th><th>Email</th><th>Role</th><th>Assigned locations</th></tr></thead>
          <tbody>
            {admins.length === 0 ? (
              <tr><td colSpan={4} className="helper">No admin records found.</td></tr>
            ) : admins.map((admin) => {
              const displayName = [admin.firstName, admin.lastName].filter(Boolean).join(' ') || admin.email
              return (
                <tr key={admin.email} onClick={() => { setProfileEmail(admin.email); setProfileFirst(admin.firstName || ''); setProfileLast(admin.lastName || ''); setProfileRole(admin.role || 'admin') }}>
                  <td>{displayName}</td>
                  <td>{admin.email}</td>
                  <td>{admin.role || 'admin'}</td>
                  <td>{assignmentCountByEmail.get(admin.email.toLowerCase()) || 0}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="mapper-grid">
        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Admin profile</h3>
          <div className="form-row"><label htmlFor="profileEmail">Email</label><input id="profileEmail" className="input" type="email" value={profileEmail} onChange={(event) => setProfileEmail(event.target.value)} /></div>
          <div className="mapper-grid">
            <div className="form-row"><label htmlFor="profileFirst">First name</label><input id="profileFirst" className="input" value={profileFirst} onChange={(event) => setProfileFirst(event.target.value)} /></div>
            <div className="form-row"><label htmlFor="profileLast">Last name</label><input id="profileLast" className="input" value={profileLast} onChange={(event) => setProfileLast(event.target.value)} /></div>
          </div>
          <div className="form-row"><label htmlFor="profileRole">Role</label><input id="profileRole" className="input" value={profileRole} onChange={(event) => setProfileRole(event.target.value)} /></div>
          <button type="button" className="btn btn-primary" onClick={saveProfile} disabled={loading}>Save profile</button>
        </article>

        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Corporate assignments</h3>
          <div className="form-row"><label htmlFor="corporateEmail">Corporate email</label><input id="corporateEmail" className="input" type="email" value={corporateEmail} onChange={(event) => setCorporateEmail(event.target.value)} /></div>
          <div className="form-row"><label htmlFor="assignmentText">Location keys</label><input id="assignmentText" className="input" value={assignmentText} onChange={(event) => setAssignmentText(event.target.value)} placeholder={locations.map((location) => location.key).slice(0, 2).join(', ')} /></div>
          <button type="button" className="btn btn-primary" onClick={saveCorporate} disabled={loading}>Save corporate user</button>
        </article>

        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Job report</h3>
          <select className="input" value={reportLocation} onChange={(event) => setReportLocation(event.target.value)}>
            {locations.map((location) => <option key={location.key} value={location.key}>{location.label || location.key}</option>)}
          </select>
          <textarea className="input" rows={5} value={reportContent} onChange={(event) => setReportContent(event.target.value)} placeholder="Report contents" />
          <button type="button" className="btn btn-primary" onClick={uploadReport} disabled={!reportLocation || !reportContent.trim() || loading}>Upload report</button>
        </article>

        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Payroll export</h3>
          <button type="button" className="btn btn-secondary" onClick={generatePayroll} disabled={loading}>Generate export</button>
          <textarea className="input" rows={6} value={payrollCsv} readOnly />
        </article>

        <article className="panel workspace-panel stack" style={{ background: 'var(--surface-muted)' }}>
          <h3 style={{ margin: 0 }}>Breadcrumb audit</h3>
          <div className="form-row"><label htmlFor="breadcrumbEmail">Admin email filter</label><input id="breadcrumbEmail" className="input" type="email" value={breadcrumbEmail} onChange={(event) => setBreadcrumbEmail(event.target.value)} /></div>
          <select className="input" value={breadcrumbLocation} onChange={(event) => setBreadcrumbLocation(event.target.value)}>
            <option value="">All locations</option>
            {locations.map((location) => <option key={location.key} value={location.key}>{location.label || location.key}</option>)}
          </select>
          <button type="button" className="btn btn-secondary" onClick={loadBreadcrumbs} disabled={loading}>Load breadcrumbs</button>
          <div className="panel" style={{ overflowX: 'auto', maxHeight: 220 }}>
            <table className="workspace-table">
              <thead><tr><th>Worker</th><th>Location</th><th>Recorded</th><th>Lat/Lon</th></tr></thead>
              <tbody>
                {breadcrumbRows.length === 0 ? (
                  <tr><td colSpan={4} className="helper">No breadcrumb records loaded.</td></tr>
                ) : breadcrumbRows.map((row) => (
                  <tr key={row.id || `${row.phone}-${row.recordedAt}`}>
                    <td>{row.workerName || row.phone || 'Unknown worker'}</td>
                    <td>{row.locationLabel || '-'}</td>
                    <td>{row.recordedAt ? new Date(row.recordedAt).toLocaleString() : '-'}</td>
                    <td>{row.lat ?? '-'}, {row.lon ?? '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </div>
    </section>
  )
}
